import type { ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import InterventionTab from '../InterventionTab';
import HeadlineBand from './HeadlineBand';
import { useSubTabView, useSubTabScenario } from './SubTabContext';
import { api } from '../../api/client';
import ProvenanceChip from '../ProvenanceChip';
import { formatLocalTime } from '../../format';

interface AiLogic { description: string; modality: Modality; hitlLevel: HitlLevel; moat: Moat }

// §13.2/§14: the trust class + learning property come from the backend
// (/api/engines), not hardcoded in the frontend. Look the active intervention
// up by its sub-tab id and merge its class/learning/state into the AI-logic panel.
function useEnrichedAiLogic(view: string | null, aiLogic: AiLogic) {
  const engines = useQuery({ queryKey: ['engines'], queryFn: api.engines, staleTime: 60_000 });
  if (!view || !engines.data) return aiLogic;
  for (const e of engines.data) {
    const iv = e.interventions.find((i) => i.id === view);
    if (iv) {
      return {
        ...aiLogic,
        modalityClass: iv.modality_class,
        learning: iv.learning,
        learningState: iv.learning_state ?? undefined,
      };
    }
  }
  return aiLogic;
}

// Renders the per-view headline band above sub-tab content when we know the
// view id (from SubTabContext) and have data to interpret.
function bandFor(view: string | null, data: unknown): ReactNode {
  if (!view || data == null || typeof data !== 'object') return null;
  return <HeadlineBand view={view} state={data as Record<string, unknown>} />;
}

type Modality = 'deterministic' | 'ml' | 'rag' | 'genai' | 'rpa';
type HitlLevel = 'L1 sampling' | 'L2 exception review' | 'L3 approval gating' | 'L4 human decision';
type Moat = 'core' | 'commodity';

interface DataInterventionProps<T> {
  objective: string;
  workflow: string[];
  decisionRules: { rules: string[]; vetoes?: string[] };
  aiLogic: { description: string; modality: Modality; hitlLevel: HitlLevel; moat: Moat };
  kpi: { primary: string; downstream?: string; owner: string };
  queryKey: (string | number)[];
  fetcher: () => Promise<T>;
  render: (data: T) => ReactNode;
}

/**
 * The data-driven sub-tab: five-part InterventionTab header + a fetched data
 * panel. Used by the value-chain hygiene/operating interventions that read a
 * single GET endpoint and present its KPIs. Lighthouse/flagship sub-tabs that
 * need bespoke interaction get their own component instead.
 */
export default function DataIntervention<T>({
  objective, workflow, decisionRules, aiLogic, kpi, queryKey, fetcher, render,
}: DataInterventionProps<T>) {
  const q = useQuery({ queryKey, queryFn: fetcher });
  const view = useSubTabView();
  const enrichedAiLogic = useEnrichedAiLogic(view, aiLogic);
  return (
    <InterventionTab
      objective={objective}
      workflow={workflow}
      decisionRules={decisionRules}
      aiLogic={enrichedAiLogic}
      kpi={kpi}
      content={
        q.isLoading ? (
          <div className="panel"><div className="small faint">Loading…</div></div>
        ) : q.isError || !q.data ? (
          <div className="panel"><div className="small" style={{ color: 'var(--red)' }}>Could not load data.</div></div>
        ) : (
          <>
            {bandFor(view, q.data)}
            {render(q.data)}
          </>
        )
      }
    />
  );
}

interface ActionInterventionProps<T> {
  objective: string;
  workflow: string[];
  decisionRules: { rules: string[]; vetoes?: string[] };
  aiLogic: { description: string; modality: Modality; hitlLevel: HitlLevel; moat: Moat };
  kpi: { primary: string; downstream?: string; owner: string };
  initialState?: Record<string, unknown>;
  headlineView?: string;
  // The input control(s) the operator edits before running
  input: ReactNode;
  runLabel: string;                  // working-verb button label, e.g. "Run profit-at-risk"
  action: () => Promise<T>;          // the mutation
  render: (data: T) => ReactNode;    // render the result
}

/**
 * The interactive sub-tab: five-part header + an input control + a run button
 * (mutation) + result panel. For POST endpoints where the operator supplies a
 * parameter then acts. Button labels must be working verbs (handoffV4 §11.2).
 */
export function ActionIntervention<T>({
  objective, workflow, decisionRules, aiLogic, kpi, initialState, headlineView, input, runLabel, action, render,
}: ActionInterventionProps<T>) {
  const m = useMutation({ mutationFn: action });
  const view = useSubTabView();
  const scenario = useSubTabScenario();
  const enrichedAiLogic = useEnrichedAiLogic(view, aiLogic);
  // §15.2: pre-run the diagnostic once when the operator arrives from an alert deep link.
  const preRan = useRef(false);
  useEffect(() => {
    if (scenario && !preRan.current) {
      preRan.current = true;
      m.mutate();
    }
  }, [scenario, m]);
  const bandState = m.data ?? initialState;
  const bandView = headlineView ?? view;
  return (
    <InterventionTab
      objective={objective}
      workflow={workflow}
      decisionRules={decisionRules}
      aiLogic={enrichedAiLogic}
      kpi={kpi}
      content={
        <div className="panel">
          {bandState ? bandFor(bandView, bandState) : null}
          <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: m.data ? 14 : 0 }}>
            <div style={{ flex: 1, minWidth: 220 }}>{input}</div>
            <button type="button" className="btn primary" onClick={() => m.mutate()} disabled={m.isPending}>
              {m.isPending ? 'Running…' : runLabel}
            </button>
          </div>
          {m.isError && <div className="small" style={{ color: 'var(--red)', marginTop: 10 }}>Run failed. Try again.</div>}
          {m.data ? (
            <div style={{ marginTop: 14 }}>
              {render(m.data)}
            </div>
          ) : null}
        </div>
      }
    />
  );
}

/* ───── Shared building blocks for sub-tab content ───── */

type TileColor = 'red' | 'amber' | 'gold' | 'olive' | 'blue' | 'teal' | 'ink' | 'muted';
const TILE: Record<TileColor, string> = {
  red: 'var(--red)', amber: 'var(--amber)', gold: 'var(--gold)', olive: 'var(--olive)',
  blue: 'var(--blue)', teal: 'var(--teal)', ink: 'var(--ink)', muted: 'var(--muted)',
};

export function MetricTiles({ tiles }: { tiles: Array<{ label: string; value: string; sub?: string; color?: TileColor }> }) {
  const cols = Math.min(tiles.length, 4);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 14 }}>
      {tiles.map((t) => (
        <div key={t.label} style={{ padding: '12px 14px', background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 8 }}>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{t.label}</div>
          <div className="num" style={{ fontSize: 22, color: TILE[t.color ?? 'ink'] }}>{t.value}</div>
          {t.sub && <div className="small faint" style={{ marginTop: 2 }}>{t.sub}</div>}
          <div style={{ marginTop: 6 }}><ProvenanceChip coverage="partial" /></div>
        </div>
      ))}
    </div>
  );
}

export interface Column<R> {
  header: string;
  align?: 'left' | 'right';
  cell: (row: R) => ReactNode;
}

export function MiniTable<R>({ title, rows, columns, rowKey }: { title?: string; rows: R[]; columns: Column<R>[]; rowKey: (row: R, i: number) => string }) {
  return (
    <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
      {title && (
        <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</div>
      )}
      <table className="c2-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.header} style={{ textAlign: c.align ?? 'left' }}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={rowKey(row, i)}>
              {columns.map((c) => (
                <td key={c.header} style={{ textAlign: c.align ?? 'left' }}>{c.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function statusColor(status: string): TileColor {
  const s = status.toLowerCase();
  if (s === 'green' || s === 'ok' || s === 'pass') return 'olive';
  if (s === 'amber' || s === 'flagged' || s === 'warn') return 'gold';
  if (s === 'red' || s === 'fail' || s === 'blocked') return 'red';
  return 'muted';
}

export function StatusPill({ status }: { status: string }) {
  const c = statusColor(status);
  const tone = c === 'olive' ? 'green' : c === 'gold' ? 'amber' : c === 'red' ? 'red' : 'muted';
  return <span className={`status-pill ${tone}`}>{status}</span>;
}

/** A small "as-of" / "est." provenance marker (handoffV4 §9.3 / §11.3). */
export function Provenance({ asOf, estimated }: { asOf?: string; estimated?: boolean }) {
  return (
    <div className="small faint" style={{ fontSize: 11, marginTop: 8, fontFamily: '"JetBrains Mono", monospace' }}>
      {estimated ? 'est.' : null}
      {estimated && asOf ? ' · ' : null}
      {asOf ? `as of ${formatLocalTime(asOf)}` : null}
    </div>
  );
}
